/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : LRtoComponentTemplate.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { componentStyle } from "../../../config/appTheme";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import CustomCheckBoxV2 from "../../../components/customComponentsV2/common/checkBox/CustomCheckBoxV2";
import { useDispatch, useSelector } from "react-redux";
import softwareIntersectionAPI from "../../../redux/actions/softwareIntersectionActions";
import CustomSearchbarWithApiV2 from "../../../components/customComponentsV2/common/searchBar/CustomSearchbarWithApiV2";
import CustomPaginationV2 from "../../../components/customComponentsV2/common/pagination/CustomPaginationV2";
import { HandleApiActions } from "../../../redux/actions";
import CustomButtonV2 from "../../../components/customComponentsV2/common/button/CustomButtonV2";
import { useNavigate } from "react-router-dom";
import CustomToolTip from "../../../components/infoButton/CustomToolTip";
import PropTypes from "prop-types";

function LRtoComponentTemplate({ selectedLrId }) { // NOSONAR
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const selectedProjectDetails = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );
  const selectedVariantDetails = useSelector(
    (state) => state?.projectConfigReducer?.selectedVariantDetails,
  );

  const [expandedSections, setExpandedSections] = useState({
    onBoard: true,
    eeComponents: false,
    softwareModules: false,
    vehicleSystems: true,
    vehicleComponents: false,
  });
  const [eeComponentsList, setEEComponentsList] = useState([]);
  const [eeTotalCount, setEETotalCount] = useState(0);
  const [eePage, setEEPage] = useState(1);
  const [eeRowsPerPage] = useState(25);
  const [swmComponentsList, setSWMComponentsList] = useState([]);
  const [swmTotalCount, setSWMTotalCount] = useState(0);
  const [swmPage, setSWMPage] = useState(1);
  const [swmRowsPerPage] = useState(25);
  const [vsComponentsList, setVSComponentsList] = useState([]);
  const [vsTotalCount, setVSTotalCount] = useState(0);
  const [vsPage, setVSPage] = useState(1);
  const [vsRowsPerPage] = useState(25);
  const [selectedVSDetails, setSelectedVSDetails] = useState(null);
  const [vcComponentsList, setVCComponentsList] = useState([]);
  const [vcTotalCount, setVCTotalCount] = useState(0);
  const [vcPage, setVCPage] = useState(1);
  const [vcRowsPerPage] = useState(25);
  const [standaloneVCList, setStandaloneVCList] = useState([]);
  const [standaloneVCTotalCount, setStandaloneVCTotalCount] = useState(0);
  const [standaloneVCPage, setStandaloneVCPage] = useState(1);
  const [standaloneVCRowsPerPage] = useState(25);

  useEffect(() => {
    setSelectedVSDetails(null);
    setVCComponentsList([]);
    setVCTotalCount(0);
    setVCPage(1);
    setStandaloneVCList([]);
    setStandaloneVCTotalCount(0);
    setStandaloneVCPage(1);
  }, [
    selectedLrId,
    selectedProjectDetails?.project_id,
    selectedVariantDetails?.variant_id,
  ]);

  const isExpandAll = useMemo(
    () =>
      [
        "onBoard",
        "eeComponents",
        "softwareModules",
        "vehicleSystems",
        "vehicleComponents",
      ].every((key) => expandedSections[key]),
    [expandedSections],
  );

  const handleToggle = (key) => (_event, isExpanded) => {
    setExpandedSections((prev) => {
      const next = {
        ...prev,
        [key]: isExpanded,
      };

      if (key === "onBoard" && !isExpanded) {
        next.eeComponents = false;
        next.softwareModules = false;
        next.vehicleSystems = false;
        next.vehicleComponents = false;
      }

      if (key !== "onBoard" && isExpanded) {
        next.onBoard = true;
      }

      return next;
    });
  };

  const handleExpandAll = (event) => {
    const checked = event.target.checked;
    setExpandedSections((prev) => ({
      ...prev,
      onBoard: checked,
      eeComponents: checked,
      softwareModules: checked,
      vehicleSystems: checked,
      vehicleComponents: checked,
    }));
  };

  const getVCListByVS = async ({ vsId, pageNumber = 1 }) => {
    const resp = await dispatch(
      HandleApiActions({
        ...softwareIntersectionAPI.get_vc_for_legal_requirements,
        params: {
          show_details: true,
          project_id: Number(selectedProjectDetails?.project_id),
          variant_id: Number(selectedVariantDetails?.variant_id),
          ...(selectedLrId ? { lr_id: Number(selectedLrId) } : {}),
          vs_id: Number(vsId),
          page_number: Number(pageNumber),
          page_size: Number(vcRowsPerPage),
        },
        show_toast: false,
        ignore_app_loading_spinner: true,
      }),
    );

    if (resp?.error) {
      setVCComponentsList([]);
      setVCTotalCount(0);
      return;
    }

    setVCComponentsList(resp?.data || []);
    setVCTotalCount(Number(resp?.total_count) || 0);
  };

  const handleOpenVSDetails = async (vs) => {
    setSelectedVSDetails(vs);
    setVCPage(1);
    await getVCListByVS({ vsId: vs?.id || vs?.vs_id, pageNumber: 1 });
  };

  const handleBackToVSList = () => {
    setSelectedVSDetails(null);
    setVCComponentsList([]);
    setVCTotalCount(0);
    setVCPage(1);
  };

  const accordionSx = {
    backgroundColor: "#000000",
    color: "#ffffff",
    boxShadow: "none",
    borderRadius: 0,
    overflow: "hidden",
    margin: 0,
    "&.Mui-expanded": {
      margin: 0,
    },
    "&.MuiPaper-root": {
      borderRadius: 0,
    },
    "&.MuiAccordion-root:first-of-type": {
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
    },
    "&.MuiAccordion-root:last-of-type": {
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    },
    "& .MuiAccordionSummary-root": {
      borderRadius: 0,
      minHeight: "44px !important",
      paddingTop: 0,
      paddingBottom: 0,
      "&.Mui-expanded": {
        minHeight: "44px !important",
      },
    },
    "& .MuiAccordionSummary-content": {
      marginTop: "10px !important",
      marginBottom: "10px !important",
      "&.Mui-expanded": {
        marginTop: "10px !important",
        marginBottom: "10px !important",
      },
    },
    "& .MuiAccordionDetails-root": {
      borderRadius: 0,
    },
    "&:before": {
      display: "none",
    },
  };

  const nestedAccordionSx = {
    backgroundColor: "#1f1f1f",
    color: "#ffffff",
    boxShadow: "none",
    borderRadius: 0,
    overflow: "hidden",
    m: 0,
    width: "100%",
    "&.Mui-expanded": {
      margin: 0,
    },
    "&.MuiPaper-root": {
      borderRadius: 0,
    },
    "&.MuiAccordion-root:first-of-type": {
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
    },
    "&.MuiAccordion-root:last-of-type": {
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    },
    "& .MuiAccordionSummary-root": {
      borderRadius: 0,
      minHeight: "44px !important",
      paddingTop: 0,
      paddingBottom: 0,
      "&.Mui-expanded": {
        minHeight: "44px !important",
      },
    },
    "& .MuiAccordionSummary-content": {
      marginTop: "10px !important",
      marginBottom: "10px !important",
      "&.Mui-expanded": {
        marginTop: "10px !important",
        marginBottom: "10px !important",
      },
    },
    "& .MuiAccordionDetails-root": {
      borderRadius: 0,
    },
    "&:before": {
      display: "none",
    },
  };

  const isVCView = Boolean(selectedVSDetails);
  const vehiclePageValue = isVCView ? vcPage : vsPage;
  const vehicleRowsPerPageValue = isVCView ? vcRowsPerPage : vsRowsPerPage;
  const vehicleTotalItems = isVCView ? vcTotalCount : vsTotalCount;

  const renderVehicleSystemsContent = () => {
    if (selectedVSDetails) {
      return (
        <Box>
          <Box sx={{ mb: 1, display: "flex", alignItems: "center" }}>
            <IconButton
              size="small"
              onClick={handleBackToVSList}
              sx={{ p: 0.25, mr: 0.5 }}
            >
              <ArrowBackIcon sx={{ fontSize: "20px", color: "#000000" }} />
            </IconButton>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              color: "#000000",
              borderBottom: "1px solid #E2E2E2",
              p: "0.5rem 0 0.5rem 0",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <Typography sx={{ ...componentStyle.font.text }}>
                <b>Vehicle System:</b>
              </Typography>
              <CustomToolTip
                style={{
                  ...componentStyle.font.text,
                  fontWeight: 800,
                }}
                length={60}
              >
                Name: {selectedVSDetails?.name || "-"}
              </CustomToolTip>
            </Box>
            <Box>
              {selectedProjectDetails?.is_zonal && (
                <Typography sx={{ ...componentStyle.font.text }}>
                  <b>
                    Zone: {selectedVSDetails?.zone_details?.zone_name || "-"}
                  </b>
                </Typography>
              )}
            </Box>
          </Box>

          {vcComponentsList.length > 0 ? (
            vcComponentsList.map((vc) => (
              <Box
                key={vc.id}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  color: "#000000",
                  borderBottom: "1px solid #E2E2E2",
                  p: "0.5rem 0 0.5rem 0",
                }}
              >
                <Typography sx={{ ...componentStyle.font.text }}>
                  <b>Vehicle Component:</b>
                </Typography>
                <CustomToolTip
                  style={{
                    ...componentStyle.font.text,
                    fontWeight: 800,
                  }}
                  length={60}
                >
                  Component No: {vc?.component_number || "-"}
                </CustomToolTip>
                <CustomToolTip
                  style={{
                    ...componentStyle.font.text,
                  }}
                  length={60}
                >
                  Name: {vc?.name || "-"}
                </CustomToolTip>
                <CustomToolTip
                  style={{
                    ...componentStyle.font.text,
                    textAlign: "left",
                  }}
                  length={60}
                >
                  Description: {vc?.description || "-"}
                </CustomToolTip>
              </Box>
            ))
          ) : (
            <Box sx={{ color: "#000000", pt: 1 }}>
              <Typography sx={{ ...componentStyle.font.text }}>
                No vehicle components found.
              </Typography>
            </Box>
          )}
        </Box>
      );
    }

    if (vsComponentsList.length > 0) {
      return (
        <Box>
          {vsComponentsList.map((vs) => (
            <Box
              key={vs.id}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                color: "#000000",
                borderBottom: "1px solid #E2E2E2",
                p: "0.5rem 0 0.5rem 0",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                {selectedProjectDetails?.is_zonal && (
                  <Typography sx={{ ...componentStyle.font.text }}>
                    <b>Zone: {vs?.zone_details?.zone_name || "-"}</b>
                  </Typography>
                )}
                <Typography sx={{ ...componentStyle.font.text }}>
                  <b>Assembly No: {vs?.assembly_number || "-"}</b>
                </Typography>
                <Typography sx={{ ...componentStyle.font.text }}>
                  Name: {vs?.name || "-"}
                </Typography>
                <Typography
                  sx={{ ...componentStyle.font.text, textAlign: "left" }}
                >
                  Description: {vs?.description || "-"}
                </Typography>
              </Box>
              <Box>
                <IconButton
                  size="small"
                  onClick={() => handleOpenVSDetails(vs)}
                >
                  <KeyboardArrowRightIcon sx={{ color: "#000000" }} />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Box>
      );
    }

    return (
      <Box sx={{ color: "#000000" }}>
        <Typography sx={{ ...componentStyle.font.text }}>
          No vehicle systems found.
        </Typography>
      </Box>
    );
  };

  return (
    <Box>
      <Grid item sx={{ mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography
              sx={{ ...componentStyle.font.tabLevelHeader, textAlign: "left" }}
            >
              Linked Architecture
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              mr: 1,
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <CustomCheckBoxV2
                value={isExpandAll}
                onChange={handleExpandAll}
              />
              <Typography
                sx={{ ...componentStyle.font.text, fontSize: "15px", ml: -2 }}
              >
                Expand All
              </Typography>
            </Box>

            <CustomButtonV2
              disabled={
                !selectedProjectDetails?.project_id ||
                !selectedVariantDetails?.variant_id
              }
              btnName="Create Ticket"
              handleOnClick={() => {
                navigate("/dashboard/v2/type-approval/link/create?tab=0");
              }}
            />
          </Box>
        </Box>
      </Grid>

      <Grid
        container
        sx={
          {
          }
        }
      >
        <Grid
          item
          sm={12}
          sx={{
            minHeight: "390px",
            maxHeight: "390px",
            overflowY: "scroll",
            overflowX: "hidden",
            ...componentStyle.scroll,
          }}
        >
          <Accordion
            expanded={expandedSections.onBoard}
            onChange={handleToggle("onBoard")}
            sx={accordionSx}
          >
            <AccordionSummary
              expandIcon={<KeyboardArrowDownIcon sx={{ color: "#ffffff" }} />}
            >
              <Typography
                sx={{ ...componentStyle.font.cardHeadder, color: "#ffffff" }}
              >
                On-Board
              </Typography>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                backgroundColor: "#f5f6fa",
                p: 0,
                m: 0,
                width: "100%",
                borderTop: "1px solid #3a3a3a",
                "& > .MuiAccordion-root:not(:last-of-type)": {
                  borderBottom: "1px solid #3a3a3a",
                },
              }}
            >
              <Accordion
                expanded={expandedSections.vehicleSystems}
                onChange={handleToggle("vehicleSystems")}
                sx={{
                  ...nestedAccordionSx,
                  "& .MuiAccordionSummary-root": {
                    borderRadius: 0,
                    minHeight: expandedSections.vehicleSystems
                      ? "56px !important"
                      : "44px !important",
                    paddingTop: 0,
                    paddingBottom: 0,
                    "&.Mui-expanded": {
                      minHeight: "56px !important",
                    },
                  },
                  "& .MuiAccordionSummary-content": {
                    marginTop: "8px !important",
                    marginBottom: "8px !important",
                    "&.Mui-expanded": {
                      marginTop: "8px !important",
                      marginBottom: "8px !important",
                    },
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <KeyboardArrowDownIcon sx={{ color: "#ffffff" }} />
                  }
                >
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      pr: 1,
                      gap: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        ...componentStyle.font.cardText,
                        color: "#ffffff",
                        minWidth: "fit-content",
                        ml: 1.5,
                      }}
                    >
                      Vehicle Systems
                    </Typography>
                    {expandedSections.vehicleSystems ? (
                      <Box
                        sx={{
                          width: { xs: "160px", sm: "220px", md: "280px" },
                        }}
                        onClick={(event) => event.stopPropagation()}
                        onFocus={(event) => event.stopPropagation()}
                      >
                        <CustomSearchbarWithApiV2
                          key={
                            selectedVSDetails
                              ? `vc-search-${selectedVSDetails?.id || selectedVSDetails?.vs_id}`
                              : "vs-search"
                          }
                          apiAction={
                            selectedVSDetails
                              ? softwareIntersectionAPI.get_vc_for_legal_requirements
                              : softwareIntersectionAPI.get_vs_for_legal_requirements
                          }
                          searchKey={"search_name"}
                          placeholder={"Search"}
                          iconColor={"whitesmoke"}
                          txtFldStyle={{
                            "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                              borderColor: "whitesmoke",
                            },
                            "& .MuiInputBase-input": {
                              color: "whitesmoke",
                              WebkitTextFillColor: "whitesmoke",
                              caretColor: "whitesmoke",
                            },
                          }}
                          getDataOnSearch={(data, resp) => {
                            if (resp?.error) {
                              if (selectedVSDetails) {
                                setVCComponentsList([]);
                                setVCTotalCount(0);
                              } else {
                                setVSComponentsList([]);
                                setVSTotalCount(0);
                              }
                              return;
                            }

                            if (selectedVSDetails) {
                              setVCComponentsList(data || []);
                              setVCTotalCount(Number(resp?.total_count) || 0);
                            } else {
                              setVSComponentsList(data || []);
                              setVSTotalCount(Number(resp?.total_count) || 0);
                            }
                          }}
                          onSearchQueryChange={(query, prevQuery) => {
                            if (query !== prevQuery) {
                              if (selectedVSDetails) {
                                setVCPage(1);
                              } else {
                                setVSPage(1);
                              }
                            }
                          }}
                          params={{
                            show_details: true,
                            project_id: Number(
                              selectedProjectDetails?.project_id,
                            ),
                            variant_id: Number(
                              selectedVariantDetails?.variant_id,
                            ),
                            ...(selectedLrId
                              ? { lr_id: Number(selectedLrId) }
                              : {}),
                            ...(selectedVSDetails
                              ? {
                                  vs_id: Number(
                                    selectedVSDetails?.id ||
                                      selectedVSDetails?.vs_id,
                                  ),
                                  page_number: vcPage,
                                  page_size: vcRowsPerPage,
                                }
                              : {
                                  page_number: vsPage,
                                  page_size: vsRowsPerPage,
                                }),
                          }}
                        />
                      </Box>
                    ) : null}
                  </Box>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    backgroundColor: "#ffffff",
                    p: 0,
                    height: { xs: "260px", sm: "320px" },
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      p: 1,
                      flex: 1,
                      overflowY: "auto",
                      "&::-webkit-scrollbar": {
                        width: "8px",
                      },
                      "&::-webkit-scrollbar-track": {
                        background: "#f1f1f1",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        background: "#b0b0b0",
                      },
                      "&::-webkit-scrollbar-thumb:hover": {
                        background: "#8d8d8d",
                      },
                    }}
                  >
                    {renderVehicleSystemsContent()}
                  </Box>

                  <Box
                    sx={{
                      borderTop: "1px solid #E2E2E2",
                      p: 0.5,
                    }}
                  >
                    <CustomPaginationV2
                      page={vehiclePageValue}
                      rowsPerPage={vehicleRowsPerPageValue}
                      totalItems={vehicleTotalItems}
                      onPageChange={(newPage) => {
                        if (selectedVSDetails) {
                          setVCPage(newPage);
                        } else {
                          setVSPage(newPage);
                        }
                      }}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expandedSections.vehicleComponents}
                onChange={handleToggle("vehicleComponents")}
                sx={{
                  ...nestedAccordionSx,
                  "& .MuiAccordionSummary-root": {
                    borderRadius: 0,
                    minHeight: expandedSections.vehicleComponents
                      ? "56px !important"
                      : "44px !important",
                    paddingTop: 0,
                    paddingBottom: 0,
                    "&.Mui-expanded": {
                      minHeight: "56px !important",
                    },
                  },
                  "& .MuiAccordionSummary-content": {
                    marginTop: "8px !important",
                    marginBottom: "8px !important",
                    "&.Mui-expanded": {
                      marginTop: "8px !important",
                      marginBottom: "8px !important",
                    },
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <KeyboardArrowDownIcon sx={{ color: "#ffffff" }} />
                  }
                >
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      pr: 1,
                      gap: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        ...componentStyle.font.cardText,
                        color: "#ffffff",
                        minWidth: "fit-content",
                        ml: 1.5,
                      }}
                    >
                      Vehicle Components
                    </Typography>
                    {expandedSections.vehicleComponents ? (
                      <Box
                        sx={{
                          width: { xs: "160px", sm: "220px", md: "280px" },
                        }}
                        onClick={(event) => event.stopPropagation()}
                        onFocus={(event) => event.stopPropagation()}
                      >
                        <CustomSearchbarWithApiV2
                          apiAction={
                            softwareIntersectionAPI.get_vc_for_legal_requirements
                          }
                          searchKey={"search_name"}
                          placeholder={"Search"}
                          iconColor={"whitesmoke"}
                          txtFldStyle={{
                            "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                              borderColor: "whitesmoke",
                            },
                            "& .MuiInputBase-input": {
                              color: "whitesmoke",
                              WebkitTextFillColor: "whitesmoke",
                              caretColor: "whitesmoke",
                            },
                          }}
                          getDataOnSearch={(data, resp) => {
                            if (resp?.error) {
                              setStandaloneVCList([]);
                              setStandaloneVCTotalCount(0);
                              return;
                            }
                            setStandaloneVCList(data || []);
                            setStandaloneVCTotalCount(
                              Number(resp?.total_count) || 0,
                            );
                          }}
                          onSearchQueryChange={(query, prevQuery) => {
                            if (query !== prevQuery) {
                              setStandaloneVCPage(1);
                            }
                          }}
                          params={{
                            show_details: true,
                            project_id: Number(
                              selectedProjectDetails?.project_id,
                            ),
                            variant_id: Number(
                              selectedVariantDetails?.variant_id,
                            ),
                            ...(selectedLrId
                              ? { lr_id: Number(selectedLrId) }
                              : {}),
                            page_number: standaloneVCPage,
                            page_size: standaloneVCRowsPerPage,
                          }}
                        />
                      </Box>
                    ) : null}
                  </Box>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    backgroundColor: "#ffffff",
                    p: 0,
                    height: { xs: "260px", sm: "320px" },
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      p: 1,
                      flex: 1,
                      overflowY: "auto",
                      "&::-webkit-scrollbar": { width: "8px" },
                      "&::-webkit-scrollbar-track": { background: "#f1f1f1" },
                      "&::-webkit-scrollbar-thumb": { background: "#b0b0b0" },
                      "&::-webkit-scrollbar-thumb:hover": {
                        background: "#8d8d8d",
                      },
                    }}
                  >
                    {standaloneVCList.length > 0 ? (
                      standaloneVCList.map((vc) => (
                        <Box
                          key={vc.id || vc.vc_id}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            color: "#000000",
                            borderBottom: "1px solid #E2E2E2",
                            p: "0.5rem 0 0.5rem 0",
                          }}
                        >
                          {(selectedProjectDetails?.is_zonal ||
                            vc?.vs_details?.zone_details) && (
                            <Typography sx={{ ...componentStyle.font.text }}>
                              <b>
                                Zone:{" "}
                                {vc?.vs_details?.zone_details?.position ||
                                  vc?.vs_details?.zone_details?.zone_name ||
                                  "-"}
                              </b>
                            </Typography>
                          )}
                          <Typography sx={{ ...componentStyle.font.text }}>
                            <b>Vehicle System:</b>
                          </Typography>
                          <CustomToolTip
                            style={{ ...componentStyle.font.text }}
                            length={60}
                          >
                            Name: {vc?.vs_details?.name || "-"}
                          </CustomToolTip>
                          <Typography sx={{ ...componentStyle.font.text }}>
                            <b>Vehicle Component:</b>
                          </Typography>
                          <CustomToolTip
                            style={{
                              ...componentStyle.font.text,
                              fontWeight: 800,
                            }}
                            length={60}
                          >
                            Component No: {vc?.component_number || "-"}
                          </CustomToolTip>
                          <CustomToolTip
                            style={{
                              ...componentStyle.font.text,
                            }}
                            length={60}
                          >
                            Name: {vc?.name || "-"}
                          </CustomToolTip>

                          <CustomToolTip
                            style={{
                              ...componentStyle.font.text,
                              textAlign: "left",
                            }}
                            length={60}
                          >
                            Description: {vc?.description || "-"}
                          </CustomToolTip>
                        </Box>
                      ))
                    ) : (
                      <Box sx={{ color: "#000000" }}>
                        <Typography sx={{ ...componentStyle.font.text }}>
                          No vehicle components found.
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Box sx={{ borderTop: "1px solid #E2E2E2", p: 0.5 }}>
                    <CustomPaginationV2
                      page={standaloneVCPage}
                      rowsPerPage={standaloneVCRowsPerPage}
                      totalItems={standaloneVCTotalCount}
                      onPageChange={(newPage) => {
                        setStandaloneVCPage(newPage);
                      }}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
              <Accordion
                expanded={expandedSections.eeComponents}
                onChange={handleToggle("eeComponents")}
                sx={{
                  ...nestedAccordionSx,
                  "& .MuiAccordionSummary-root": {
                    borderRadius: 0,
                    minHeight: expandedSections.eeComponents
                      ? "56px !important"
                      : "44px !important",
                    paddingTop: 0,
                    paddingBottom: 0,
                    "&.Mui-expanded": {
                      minHeight: "56px !important",
                    },
                  },
                  "& .MuiAccordionSummary-content": {
                    marginTop: "8px !important",
                    marginBottom: "8px !important",
                    "&.Mui-expanded": {
                      marginTop: "8px !important",
                      marginBottom: "8px !important",
                    },
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <KeyboardArrowDownIcon sx={{ color: "#ffffff" }} />
                  }
                >
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      pr: 1,
                      gap: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        ...componentStyle.font.cardText,
                        color: "#ffffff",
                        minWidth: "fit-content",
                        ml: 1.5,
                      }}
                    >
                      EE Components
                    </Typography>
                    {expandedSections.eeComponents ? (
                      <Box
                        sx={{
                          width: { xs: "160px", sm: "220px", md: "280px" },
                        }}
                        onClick={(event) => event.stopPropagation()}
                        onFocus={(event) => event.stopPropagation()}
                      >
                        <CustomSearchbarWithApiV2
                          apiAction={
                            softwareIntersectionAPI.get_ee_for_legal_requirements
                          }
                          searchKey={"search_name"}
                          placeholder={"Search"}
                          iconColor={"whitesmoke"}
                          txtFldStyle={{
                            "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                              borderColor: "whitesmoke",
                            },
                            "& .MuiInputBase-input": {
                              color: "whitesmoke",
                              WebkitTextFillColor: "whitesmoke",
                              caretColor: "whitesmoke",
                            },
                          }}
                          getDataOnSearch={(data, resp) => {
                            if (resp?.error) {
                              setEEComponentsList([]);
                              setEETotalCount(0);
                              return;
                            }
                            setEEComponentsList(data || []);
                            setEETotalCount(Number(resp?.total_count) || 0);
                          }}
                          onSearchQueryChange={(query, prevQuery) => {
                            if (query !== prevQuery) {
                              setEEPage(1);
                            }
                          }}
                          params={{
                            show_details: true,
                            project_id: Number(
                              selectedProjectDetails?.project_id,
                            ),
                            variant_id: Number(
                              selectedVariantDetails?.variant_id,
                            ),
                            ...(selectedLrId
                              ? { lr_id: Number(selectedLrId) }
                              : {}),
                            page_number: eePage,
                            page_size: eeRowsPerPage,
                          }}
                        />
                      </Box>
                    ) : null}
                  </Box>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    backgroundColor: "#ffffff",
                    p: 0,
                    height: { xs: "260px", sm: "320px" },
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      p: 1,
                      flex: 1,
                      overflowY: "auto",
                      "&::-webkit-scrollbar": {
                        width: "8px",
                      },
                      "&::-webkit-scrollbar-track": {
                        background: "#f1f1f1",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        background: "#b0b0b0",
                      },
                      "&::-webkit-scrollbar-thumb:hover": {
                        background: "#8d8d8d",
                      },
                    }}
                  >
                    {eeComponentsList.length > 0 ? (
                      eeComponentsList.map((ee) => (
                        <Box
                          key={ee.ee_id}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            color: "#000000",
                            borderBottom: "1px solid #E2E2E2",
                            p: "0.5rem 0 0.5rem 0",
                          }}
                        >
                          {selectedProjectDetails?.is_zonal && (
                            <Typography sx={{ ...componentStyle.font.text }}>
                              <b>Zone: {ee?.zone_details.zone_name || "-"}</b>
                            </Typography>
                          )}
                          <CustomToolTip
                            style={{
                              ...componentStyle.font.text,
                              fontWeight: 800,
                            }}
                            length={60}
                          >
                            Assembly Number: {ee?.assembly_number}
                          </CustomToolTip>
                          <Typography sx={{ ...componentStyle.font.text }}>
                            Version: {ee?.version || "-"}
                          </Typography>
                          <CustomToolTip
                            style={{
                              ...componentStyle.font.text,
                              textAlign: "left",
                            }}
                            length={60}
                          >
                            Description: {ee?.description || "-"}
                          </CustomToolTip>
                        </Box>
                      ))
                    ) : (
                      <Box sx={{ color: "#000000" }}>
                        <Typography sx={{ ...componentStyle.font.text }}>
                          No EE components found.
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Box
                    sx={{
                      borderTop: "1px solid #E2E2E2",
                      p: 0.5,
                    }}
                  >
                    <CustomPaginationV2
                      page={eePage}
                      rowsPerPage={eeRowsPerPage}
                      totalItems={eeTotalCount}
                      onPageChange={(newPage) => {
                        setEEPage(newPage);
                      }}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expandedSections.softwareModules}
                onChange={handleToggle("softwareModules")}
                sx={{
                  ...nestedAccordionSx,
                  "& .MuiAccordionSummary-root": {
                    borderRadius: 0,
                    minHeight: expandedSections.softwareModules
                      ? "56px !important"
                      : "44px !important",
                    paddingTop: 0,
                    paddingBottom: 0,
                    "&.Mui-expanded": {
                      minHeight: "56px !important",
                    },
                  },
                  "& .MuiAccordionSummary-content": {
                    marginTop: "8px !important",
                    marginBottom: "8px !important",
                    "&.Mui-expanded": {
                      marginTop: "8px !important",
                      marginBottom: "8px !important",
                    },
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <KeyboardArrowDownIcon sx={{ color: "#ffffff" }} />
                  }
                >
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      pr: 1,
                      gap: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        ...componentStyle.font.cardText,
                        color: "#ffffff",
                        minWidth: "fit-content",
                        ml: 1.5,
                      }}
                    >
                      Software Modules
                    </Typography>
                    {expandedSections.softwareModules ? (
                      <Box
                        sx={{
                          width: { xs: "160px", sm: "220px", md: "280px" },
                        }}
                        onClick={(event) => event.stopPropagation()}
                        onFocus={(event) => event.stopPropagation()}
                      >
                        <CustomSearchbarWithApiV2
                          apiAction={
                            softwareIntersectionAPI.get_swm_for_legal_requirements
                          }
                          searchKey={"search_name"}
                          placeholder={"Search"}
                          iconColor={"whitesmoke"}
                          txtFldStyle={{
                            "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                              borderColor: "whitesmoke",
                            },
                            "& .MuiInputBase-input": {
                              color: "whitesmoke",
                              WebkitTextFillColor: "whitesmoke",
                              caretColor: "whitesmoke",
                            },
                          }}
                          getDataOnSearch={(data, resp) => {
                            if (resp?.error) {
                              setSWMComponentsList([]);
                              setSWMTotalCount(0);
                              return;
                            }
                            setSWMComponentsList(data || []);
                            setSWMTotalCount(Number(resp?.total_count) || 0);
                          }}
                          onSearchQueryChange={(query, prevQuery) => {
                            if (query !== prevQuery) {
                              setSWMPage(1);
                            }
                          }}
                          params={{
                            show_details: true,
                            project_id: Number(
                              selectedProjectDetails?.project_id,
                            ),
                            variant_id: Number(
                              selectedVariantDetails?.variant_id,
                            ),
                            ...(selectedLrId
                              ? { lr_id: Number(selectedLrId) }
                              : {}),
                            page_number: swmPage,
                            page_size: swmRowsPerPage,
                          }}
                        />
                      </Box>
                    ) : null}
                  </Box>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    backgroundColor: "#ffffff",
                    p: 0,
                    height: { xs: "260px", sm: "320px" },
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      p: 1,
                      flex: 1,
                      overflowY: "auto",
                      "&::-webkit-scrollbar": {
                        width: "8px",
                      },
                      "&::-webkit-scrollbar-track": {
                        background: "#f1f1f1",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        background: "#b0b0b0",
                      },
                      "&::-webkit-scrollbar-thumb:hover": {
                        background: "#8d8d8d",
                      },
                    }}
                  >
                    {swmComponentsList.length > 0 ? (
                      swmComponentsList.map((swm) => (
                        <Box
                          key={swm.swm_id}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            color: "#000000",
                            borderBottom: "1px solid #E2E2E2",
                            p: "0.5rem 0 0.5rem 0",
                          }}
                        >
                          {selectedProjectDetails?.is_zonal && (
                            <Typography sx={{ ...componentStyle.font.text }}>
                              <b>
                                Zone:{" "}
                                {swm?.ee_details?.zone_details?.zone_name ||
                                  "-"}
                              </b>
                            </Typography>
                          )}
                          <Typography sx={{ ...componentStyle.font.text }}>
                            <b>EE Component:</b>
                          </Typography>
                          <CustomToolTip
                            style={{
                              ...componentStyle.font.text,
                              textAlign: "left",
                            }}
                            length={60}
                          >
                            Description: {swm?.ee_details?.description || "-"}
                          </CustomToolTip>
                          <Typography sx={{ ...componentStyle.font.text }}>
                            <b>Software Module:</b>
                          </Typography>
                          <CustomToolTip
                            style={{
                              ...componentStyle.font.text,
                              fontWeight: 800,
                            }}
                            length={60}
                          >
                            Number: {swm?.name || "-"}
                          </CustomToolTip>
                          <Typography sx={{ ...componentStyle.font.text }}>
                            Version: {swm?.version || "-"}
                          </Typography>
                          <CustomToolTip
                            style={{
                              ...componentStyle.font.text,
                              textAlign: "left",
                            }}
                            length={60}
                          >
                            Description: {swm?.description || "-"}
                          </CustomToolTip>
                        </Box>
                      ))
                    ) : (
                      <Box sx={{ color: "#000000" }}>
                        <Typography sx={{ ...componentStyle.font.text }}>
                          No software modules found.
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Box
                    sx={{
                      borderTop: "1px solid #E2E2E2",
                      p: 0.5,
                    }}
                  >
                    <CustomPaginationV2
                      page={swmPage}
                      rowsPerPage={swmRowsPerPage}
                      totalItems={swmTotalCount}
                      onPageChange={(newPage) => {
                        setSWMPage(newPage);
                      }}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>
    </Box>
  );
}

export default LRtoComponentTemplate;

LRtoComponentTemplate.propTypes = {
  selectedLrId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};
